import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "@/components/app-shell";
import { RequireAuth, RequireRole } from "@/components/route-guards";
import { PageTitle } from "@/lib/page-title";
import type { Role } from "@/lib/session";
import { AuthPage } from "@/pages/auth";
import { CompanyDetailPage } from "@/pages/company-detail";
import { DashboardPage } from "@/pages/dashboard";
import { EmployeeLoginPage } from "@/pages/employee-login";
import { ForgotPasswordPage } from "@/pages/forgot-password";
import { EmployeesPage } from "@/pages/employees";
import { LegalLegacyRedirect, PrivacyPage, TermsPage } from "@/pages/legal";
import { LinkInvalidPage } from "@/pages/link-invalid";
import { RedeemPage } from "@/pages/redeem";
import { MyTrackingPage } from "@/pages/my-tracking";
import { MyTrackingProjectPage } from "@/pages/my-tracking-project";
import { NotFoundPage } from "@/pages/not-found";
import { ProjectDetailPage } from "@/pages/project-detail";
import { ProjectLivePage } from "@/pages/project-live";
import { ProjectNewPage } from "@/pages/project-new";
import { ProjectsPage } from "@/pages/projects";
import { AdminPage } from "@/pages/admin";
import { FestschreibungPage } from "@/pages/festschreibung";
import { SetPasswordPage } from "@/pages/set-password";

/** Signed-in surface: the shell plus the guard, optionally narrowed to a set of roles. */
function Protected({ children, roles }: { children: ReactNode; roles?: Role[] }) {
  const guarded = roles ? <RequireRole roles={roles}>{children}</RequireRole> : children;
  return (
    <RequireAuth>
      <AppShell>{guarded}</AppShell>
    </RequireAuth>
  );
}

/** Keep `?reason=` (and any other query) when `/auth/link-invalid` is bookmarked. */
function RedirectWithQuery({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

export function App() {
  return (
    <>
      <PageTitle />
      <Routes>
        {/* Public (PRD §4.7) */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/legal" element={<LegalLegacyRedirect />} />
        <Route path="/login/link-invalid" element={<LinkInvalidPage />} />
        <Route path="/login/forgot" element={<ForgotPasswordPage />} />
        <Route path="/login/redeem" element={<RedeemPage />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/auth/link-invalid" element={<RedirectWithQuery to="/login/link-invalid" />} />
        <Route path="/auth" element={<Navigate to="/login" replace />} />
        <Route path="/employee-login" element={<EmployeeLoginPage />} />
        <Route
          path="/set-password"
          element={
            <RequireAuth>
              <SetPasswordPage />
            </RequireAuth>
          }
        />

        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route
          path="/"
          element={
            <Protected>
              <DashboardPage />
            </Protected>
          }
        />
        <Route
          path="/projects"
          element={
            <Protected roles={["grantonomy_admin", "company_admin"]}>
              <ProjectsPage />
            </Protected>
          }
        />
        <Route
          path="/projects/new"
          element={
            <Protected roles={["grantonomy_admin"]}>
              <ProjectNewPage />
            </Protected>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <Protected roles={["grantonomy_admin", "company_admin"]}>
              <ProjectDetailPage />
            </Protected>
          }
        />
        <Route
          path="/projects/:id/live"
          element={
            <Protected roles={["grantonomy_admin", "company_admin"]}>
              <ProjectLivePage />
            </Protected>
          }
        />
        <Route
          path="/employees"
          element={
            <Protected roles={["grantonomy_admin", "company_admin"]}>
              <EmployeesPage />
            </Protected>
          }
        />
        <Route
          path="/companies"
          element={
            <Protected roles={["grantonomy_admin"]}>
              <RedirectWithQuery to="/projects" />
            </Protected>
          }
        />
        <Route
          path="/companies/:id"
          element={
            <Protected roles={["grantonomy_admin"]}>
              <CompanyDetailPage />
            </Protected>
          }
        />
        <Route
          path="/admin"
          element={
            <Protected roles={["grantonomy_admin"]}>
              <AdminPage />
            </Protected>
          }
        />
        <Route
          path="/festschreibung"
          element={
            <Protected roles={["company_admin"]}>
              <FestschreibungPage />
            </Protected>
          }
        />
        <Route
          path="/my/tracking"
          element={
            <Protected>
              <MyTrackingPage />
            </Protected>
          }
        />
        <Route
          path="/my/tracking/:projectId"
          element={
            <Protected>
              <MyTrackingProjectPage />
            </Protected>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
